// ============================================================
// DefenceSection - dodge, parry and the force field.
//
// Each reaction is a switch, and its details appear only once it is
// possible at all. What the sheet can say, it says: "Can Dodge" is the
// Dodge skill being trained, "Agility" and "WS" are the characteristics
// themselves (the same numbers as on the strip). What only the table can
// say - an extra dodge nobody's talent grants, a power field on a weapon
// the sheet does not list - is still a flat value pinned over the sheet,
// and shows as a pin.
// ============================================================
import type { Character } from '../../engine/character/types';
import { getEffectiveTalents } from '../../engine/character/effectiveTalents';
import { characteristicPatch } from '../../engine/character/sheetPatches';
import { trainedPatch } from '../../engine/character/skillTiers';
import { resolveDefenderSheet } from '../../engine/character/combatStatsResolver';
import type { Target } from '../../types';
import ReadoutNumber from './ReadoutNumber';
import SheetSwitch from './SheetSwitch';
import styles from './Sheet.module.css';
import type { SectionProps } from './VitalsSection';
import { isObject } from '../../engine/bodyShape';

type Props = SectionProps;

const hasTalent = (character: Character, id: string) => getEffectiveTalents(character).some((t) => t.talentId === id);

function Dodge({ character, target, onChange, onPin, sheetLocked }: Props) {
  const { dodge } = target;
  return (
    <div className={styles.reaction}>
      <SheetSwitch
        label="Can Dodge"
        checked={dodge.canDodge}
        disabled={sheetLocked}
        // The switch SHOWS the pinned answer, so setting it lets that pin go.
        onChange={(on) =>
          onChange(trainedPatch(character, 'dodge', on), undefined, [{ key: 'dodge', leaf: 'canDodge' }])
        }
      />
      {dodge.canDodge && (
        <>
          <ReadoutNumber
            label="Agility"
            min={0}
            max={200}
            value={character.characteristics.agi.value}
            disabled={sheetLocked}
            onChange={(value) => onChange(characteristicPatch(character, 'agi', 'value', value))}
          />
          <ReadoutNumber
            label="Extra Dodges"
            title={hasTalent(character, 'step_aside') ? 'Step Aside grants one' : undefined}
            min={0}
            max={5}
            value={dodge.additionalDodges || 0}
            disabled={sheetLocked}
            onChange={(value) => onPin({ key: 'dodge', leaf: 'additionalDodges' }, value)}
          />
          <span className={styles.reactionAt} data-testid="dodge-at">
            dodges at {dodge.agility}
          </span>
        </>
      )}
    </div>
  );
}

function Parry({ character, target, onChange, onPin, sheetLocked }: Props) {
  const { parry } = target;
  const pin = (leaf: string, value: unknown) => onPin({ key: 'parry', leaf }, value);
  /**
   * The sheet says it when it can: the Parry skill is trained. It cannot
   * for someone carrying only weapons that never parry (the resolver
   * refuses whatever the skill), and then the answer is pinned, as the
   * old flat switch did.
   */
  const setCanParry = (on: boolean) => {
    const patch = trainedPatch(character, 'parry', on);
    const sheetSays = resolveDefenderSheet({ ...character, ...patch }).parry.canParry;
    if (sheetSays === on) onChange(patch, undefined, [{ key: 'parry', leaf: 'canParry' }]);
    else pin('canParry', on);
  };
  return (
    <div className={styles.reaction}>
      <SheetSwitch label="Can Parry" checked={parry.canParry} disabled={sheetLocked} onChange={setCanParry} />
      {parry.canParry && (
        <>
          <ReadoutNumber
            label="WS"
            min={0}
            max={200}
            value={character.characteristics.ws.value}
            disabled={sheetLocked}
            onChange={(value) => onChange(characteristicPatch(character, 'ws', 'value', value))}
          />
          <ReadoutNumber
            label="Extra Parries"
            title={hasTalent(character, 'wall_of_steel') ? 'Wall of Steel grants one' : undefined}
            min={0}
            max={5}
            value={parry.additionalParries || 0}
            disabled={sheetLocked}
            onChange={(value) => pin('additionalParries', value)}
          />
          <span className={styles.reactionAt} data-testid="parry-at">
            parries at {parry.weaponSkill}
          </span>
          <SheetSwitch
            label="Power Field"
            title="On a successful Parry, roll d100: on 26+ the attacker's weapon is destroyed (unless it has Power Field, Natural Weapon or Warp Weapon)."
            checked={!!parry.hasPowerField}
            disabled={sheetLocked}
            onChange={(on) => pin('hasPowerField', on)}
          />
          <SheetSwitch
            label="Deflect Shot"
            hint={hasTalent(character, 'deflect_shot') ? '(talent)' : null}
            checked={!!parry.canParryRanged}
            disabled={sheetLocked}
            onChange={(on) => pin('canParryRanged', on)}
          />
        </>
      )}
    </div>
  );
}

/** The field as issued AND as it stands: switching it on by hand is also how an overloaded one is restored. */
function ForceField({ target, onChange, sheetLocked }: Props) {
  const field = target.forceField ?? { active: false, protectionRating: 0, overloadThreshold: 0 };
  const set = (patch: Partial<Target['forceField']>) => {
    const next = { ...field, ...patch };
    onChange({ forceField: next }, (state) => ({ ...state, forceField: next }));
  };
  return (
    <div className={styles.reaction}>
      <SheetSwitch
        label="Force Field"
        checked={!!field.active}
        disabled={sheetLocked}
        onChange={(active) => set({ active })}
      />
      {field.active && (
        <>
          <ReadoutNumber
            label="Prot. Rating"
            min={0}
            max={100}
            value={field.protectionRating || 0}
            disabled={sheetLocked}
            onChange={(protectionRating) => set({ protectionRating })}
          />
          <ReadoutNumber
            label="Overload"
            min={0}
            max={100}
            value={field.overloadThreshold || 0}
            disabled={sheetLocked}
            onChange={(overloadThreshold) => set({ overloadThreshold })}
          />
          {field.overloadThreshold > 0 && (
            <span className={styles.reactionAt}>a block roll of {field.overloadThreshold} or less switches it off</span>
          )}
        </>
      )}
    </div>
  );
}

/** "dodge 51 · parry 45 · field 35" for the folded caption. */
export function defenceSummary(target: Target): string {
  const parts = [
    target.dodge.canDodge ? `dodge ${target.dodge.agility}` : 'no dodge',
    target.parry.canParry ? `parry ${target.parry.weaponSkill}` : 'no parry',
    ...(target.forceField?.active ? [`field ${target.forceField.protectionRating}`] : []),
  ];
  return parts.join(' · ');
}

export default function DefenceSection(props: Props) {
  const { target, onPin, sheetLocked } = props;
  // An Object never defends (2026-10-26): its reactions would change nothing; a force field still shields it.
  if (isObject(props.character)) {
    return (
      <>
        <div className={styles.sectionNote}>An object never dodges or parries.</div>
        <ForceField {...props} />
      </>
    );
  }
  return (
    <>
      <Dodge {...props} />
      <Parry {...props} />
      <ForceField {...props} />
      <div className={styles.reaction}>
        <SheetSwitch
          label="Allow Mixed Dodge + Parry"
          checked={!!target.allowFullDefenceChain}
          disabled={sheetLocked}
          onChange={(on) => onPin({ key: 'allowFullDefenceChain' }, on)}
        />
      </div>
    </>
  );
}
